import React, { useCallback, useEffect } from "react";
import MainLayout from "../../../layouts/MainLayout";
import Form from "../../../components/forms/Form";
import { useNavigate, useLocation } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useNotification from "../../../hooks/useNotification";
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import { kitFields } from "../../../constants/forms/kitFields";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import SectionHeader from "../../../components/forms/SectionHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlFilters from "../../../hooks/useUrlFilters";

const CreateKitPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      deleted_at: false,
    },
    "equipments_kits"
  );

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange } = useForm(setDefaultFieldValues(kitFields));

  // --- Mutação para criar um novo Kit ---
  const createKitMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePost(
        entities.equipmentsKits.create,
        dataToCreate
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        navigate(`/aluguel/kits-equipamentos`, { state: { type: "success" } });
        queryClient.invalidateQueries({
          queryKey: ["equipments_kits", filters, pagination],
        });
      }
    },
    onError: (error) => {
      console.error("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        kit: {
          ...cleanedData.kit,
        },
      };
      await createKitMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar kit:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, createKitMutation]);

  const handleBack = useCallback(() => {
    navigate("/aluguel/kits-equipamentos");
  }, [navigate]);

  // --- Efeito para exibir notificações de estado da rota (PADRÃO) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Renderização do formulário ---
  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return kitFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={() => {}}
            getSelectedValue={() => {}}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Kit"
        showBackButton={true}
        backUrl="/aluguel/kits-equipamentos"
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Cadastrar Kit"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={createKitMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateKitPage;
